import { HttpClient } from '@angular/common/http';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { EnvironmentService } from '@services/environment.service';
import { lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SecurityContextService {
  private globalState: any = {};

  constructor(
    private environment: EnvironmentService,
    private http: HttpClient,
    private router: Router,
    @Optional() @SkipSelf() sharedService?: SecurityContextService,
  ) {
    if (sharedService) {
      throw new Error('SecurityContextService is already provided');
    }

    try {
      this.set(
        'authDataAdmin',
        JSON.parse(localStorage.getItem('authDataAdmin') || '{}'),
      );
    } catch {
      console.error('auth data was invalid');
      localStorage.removeItem('authDataAdmin');

      this.set('authDataAdmin', {});
    }
  }

  set = (key: string, value: any): void => {
    this.globalState[key] = value;
    localStorage.setItem(key, JSON.stringify(value));
  };

  get = (key: string): any => {
    return this.globalState[key];
  };

  authData = (): any => {
    return this.get('authDataAdmin');
  };

  getGlobalState = () => {
    return this.globalState;
  };

  logout = (): any => {
    this.set('authDataAdmin', null);
    this.router.navigate(['/login']);
  };

  login = async (username: string, password: string): Promise<any> => {
    const data =
      'grant_type=password&username=' +
      encodeURIComponent(username) +
      '&password=' +
      encodeURIComponent(password);
    const response: any = await lastValueFrom(
      this.http.post(this.environment.API_BASE + 'token', data, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }),
    );

    if (response) {
      this.set('authDataAdmin', {
        token: response.access_token,
        token_issued: response['.issued'],
        token_expires: response['.expires'],
        token_expires_in: response.expires_in,
        username: username,
        loggedin: true,
        id: response.Id,
        accountid: parseInt(response.AccountID),
        accountuserid: parseInt(response.AccountUserID),
        firstname: response.Firstname,
        lastname: response.Lastname,
        trusted: response.Trusted == '1' ? true : false,
      });
      localStorage.setItem(
        'authDataAdmin',
        JSON.stringify(this.get('authDataAdmin')),
      );
      return this.get('authDataAdmin');
    } else {
      this.logout();
    }
  };

  isTrustedUser = (): boolean => {
    return this.get('authDataAdmin')?.trusted || false;
  };

  getUserName = (): string => {
    return this.get('authDataAdmin')?.username || '';
  };

  isLoggedIn = (): boolean => {
    if (
      !this.get('authDataAdmin')?.loggedin ||
      new Date(this.get('authDataAdmin')?.token_expires).getTime() <
        new Date().getTime()
    ) {
      return false;
    }
    return true;
  };
}
